/**
 * Provides menu-related hooks so prefetch and menu state run when the app is ready.
 * Mount once (e.g. around tab content) so TabNavigator does not need side-effect imports.
 *
 * `useMenuSync` lives only on the daily menu flow screen — do not add a second instance
 * here; two hooks would each run the debounced `bookMenu` pipeline and duplicate POSTs (rate limits).
 */
import React from 'react';
import {
  useMenuBooking,
  useMenuState,
  useMenuPrefetchOnSubscriptionReady,
} from '../data/domains/menu';
import {
  useMenuBooking as useFoodMenuBooking,
  useMenuState as useFoodMenuState,
  useMenuPrefetchOnSubscriptionReady as useFoodMenuPrefetchOnSubscriptionReady,
} from '../data/domains/foodMenu';
import { useSubscriptionContext } from '../data/domains/subscription';
import { useIsFoodMenuTabFlow } from '../data/domains/config';

function StableMenuFlowProvider({ children }: { children: React.ReactNode }) {
  useMenuBooking();
  useSubscriptionContext();
  useMenuState();
  useMenuPrefetchOnSubscriptionReady();
  return <>{children}</>;
}

function FoodMenuFlowProvider({ children }: { children: React.ReactNode }) {
  useFoodMenuBooking();
  useSubscriptionContext();
  useFoodMenuState();
  useFoodMenuPrefetchOnSubscriptionReady();
  return <>{children}</>;
}

export function MenuFlowProvider({ children }: { children: React.ReactNode }) {
  const use_food_menu_stack = useIsFoodMenuTabFlow();
  if (use_food_menu_stack) {
    return <FoodMenuFlowProvider>{children}</FoodMenuFlowProvider>;
  }
  return <StableMenuFlowProvider>{children}</StableMenuFlowProvider>;
}
